import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/cn'
import { useStatusBarDim } from '../../lib/theme'
import { computePlacement, type Placement } from './placement'

/** True below the `sm` breakpoint: menus and selects become bottom sheets there. */
export function useIsMobile(): boolean {
  const q = '(max-width: 639px)'
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(q).matches)
  useEffect(() => {
    const mq = window.matchMedia?.(q)
    if (!mq) return
    const on = () => setM(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return Boolean(m)
}

/**
 * A floating panel anchored to `anchor`, positioned with `computePlacement` against the viewport
 * and re-positioned on scroll/resize. Closes on outside pointer-down and Escape (focus returns to
 * the trigger). On phones it renders as a bottom sheet instead.
 */
export function Popover({ anchor, open, onClose, children, width = 224, matchWidth = false, label, className, contentClassName }: {
  anchor: RefObject<HTMLElement | null>
  open: boolean
  onClose(): void
  children: ReactNode
  width?: number
  matchWidth?: boolean
  label?: string
  className?: string
  contentClassName?: string
}) {
  const panel = useRef<HTMLDivElement>(null)
  const mobile = useIsMobile()
  // Only the phone bottom sheet dims the page; the desktop popover has no scrim.
  useStatusBarDim(open && mobile)
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number; placement: Placement } | null>(null)

  const update = useCallback(() => {
    const a = anchor.current
    const p = panel.current
    if (!a || !p) return
    const r = a.getBoundingClientRect()
    setPos(computePlacement(r, { width, height: p.scrollHeight }, { width: window.innerWidth, height: window.innerHeight }, { matchWidth }))
  }, [anchor, width, matchWidth])

  useLayoutEffect(() => {
    if (!open || mobile) { setPos(null); return }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => { window.removeEventListener('resize', update); window.removeEventListener('scroll', update, true) }
  }, [open, mobile, update])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // The open popover owns Escape. Listening in the capture phase and stopping the event here
      // keeps it from reaching the bubble-phase listener of an enclosing Modal (which registered
      // first and would otherwise close the dialog along with the panel).
      e.stopPropagation()
      onClose()
      anchor.current?.focus()
    }
    const onDown = (e: PointerEvent) => {
      if (mobile) return
      const t = e.target as Node
      if (panel.current?.contains(t) || anchor.current?.contains(t)) return
      if (e.pointerType === 'touch') e.preventDefault()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('pointerdown', onDown)
    return () => { window.removeEventListener('keydown', onKey, true); window.removeEventListener('pointerdown', onDown) }
  }, [open, onClose, anchor, mobile])

  if (!open) return null
  if (mobile) {
    return createPortal(
      <div className="fixed inset-x-0 top-0 z-50 flex h-[var(--app-h)] flex-col justify-end">
        <button
          type="button"
          tabIndex={-1}
          aria-label={label ?? 'Close'}
          className="absolute inset-0 h-full w-full bg-[rgb(20_20_18_/_0.4)] dark:bg-[rgb(0_0_0_/_0.55)] cursor-pointer border-0 p-0"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onClose()
          }}
        />
        <div ref={panel} role="dialog" aria-label={label} className={cn('relative z-10 inset-x-0 bottom-0 max-h-[75vh] overflow-y-auto rounded-t-[22px] border-t border-line bg-cream-50 px-3 pb-6 pt-2.5 shadow-sheet', className)}>
          <div className="mx-auto mb-2 h-[5px] w-10 rounded-full bg-cream-300" />
          {children}
        </div>
      </div>,
      document.body
    )
  }
  return createPortal(
    <div
      ref={panel}
      data-placement={pos?.placement}
      style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: pos?.width ?? width, maxHeight: pos?.maxHeight, visibility: pos ? 'visible' : 'hidden' }}
      className={cn('z-50 flex flex-col overflow-hidden rounded-[10px] border border-line bg-cream-50 shadow-pop', className)}
    >
      <div className={cn('overflow-y-auto p-1', contentClassName)}>
        {children}
      </div>
    </div>,
    document.body
  )
}

export interface MenuItem {
  label: ReactNode
  onSelect(): void
  icon?: ReactNode
  shortcut?: string
  danger?: boolean
  disabled?: boolean
  /** Draw a divider above this item. */
  divider?: boolean
}

/** Button + menu (role=menu) with arrow-key navigation. */
export function Dropdown({ trigger, items, label, width = 224 }: {
  trigger: (props: { ref: RefObject<HTMLButtonElement | null>; onClick(): void; 'aria-haspopup': 'menu'; 'aria-expanded': boolean }) => ReactNode
  items: MenuItem[]
  label: string
  width?: number
}) {
  const ref = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const close = useCallback(() => setOpen(false), [])
  const list = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (open) requestAnimationFrame(() => list.current?.querySelector<HTMLButtonElement>('[role=menuitem]:not([disabled])')?.focus())
  }, [open])
  const onKey = (e: React.KeyboardEvent) => {
    const els = [...(list.current?.querySelectorAll<HTMLButtonElement>('[role=menuitem]:not([disabled])') ?? [])]
    const i = els.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === 'ArrowDown') { e.preventDefault(); els[(i + 1) % els.length]?.focus() }
    if (e.key === 'ArrowUp') { e.preventDefault(); els[(i - 1 + els.length) % els.length]?.focus() }
  }
  return (
    <>
      {trigger({ ref, onClick: () => setOpen(o => !o), 'aria-haspopup': 'menu', 'aria-expanded': open })}
      <Popover anchor={ref} open={open} onClose={close} width={width} label={label}>
        <div ref={list} role="menu" aria-label={label} onKeyDown={onKey} className="flex flex-col">
          {items.map((it, i) => (
            <div key={i}>
              {it.divider && <div className="my-1 h-px bg-line" />}
              <button
                type="button" role="menuitem" disabled={it.disabled}
                onClick={() => { close(); it.onSelect() }}
                className={cn('flex h-[34px] w-full items-center gap-2.5 rounded-[7px] px-2.5 text-left text-[13px] font-medium outline-none hover:bg-cream-200 focus:bg-cream-200 disabled:opacity-50',
                  'max-sm:h-11 max-sm:text-sm', it.danger ? 'text-red-600' : 'text-ink-100')}
              >
                {it.icon && <span className="flex h-4 w-4 items-center justify-center text-ink-400" aria-hidden="true">{it.icon}</span>}
                <span className="flex-1 truncate">{it.label}</span>
                {it.shortcut && <span className="font-mono text-[11px] text-ink-500">{it.shortcut}</span>}
              </button>
            </div>
          ))}
        </div>
      </Popover>
    </>
  )
}
